'use client';
import React, { useState } from 'react';
import {
  Users, AlertTriangle, Clock, Car, Shield,
  Printer, Search, Filter, ChevronDown, X,
  RefreshCw, Phone, CreditCard, DoorOpen,
  ArrowUpDown, CheckCircle, Building2,
} from 'lucide-react';
import { mockVisits, type Visit, type VisitorType, type GateNumber } from '@/data/mockData';
import { StatusBadge, TypeBadge } from '@/components/ui/StatusBadge';
import HourlyVisitsChart from './HourlyVisitsChart';
import { Toaster, toast } from 'sonner';

function formatTime(iso: string): string {
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

function computeDuration(entryIso: string): string {
  const entry = new Date(entryIso).getTime();
  const now = new Date('2026-08-09T11:12:07').getTime();
  const diffMs = now - entry;
  const totalMins = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  if (hours === 0) return `${mins} د`;
  if (mins === 0) return `${hours} س`;
  return `${hours}س ${mins}د`;
}

function isOverstay(visit: Visit): boolean {
  return visit.status === 'منتهية الصلاحية';
}

type SortField = 'visitorName' | 'entryTime' | 'hostDepartment' | 'type';
type SortDir = 'asc' | 'desc';

export default function CurrentlyInsideContent() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<VisitorType | 'الكل'>('الكل');
  const [filterGate, setFilterGate] = useState<GateNumber | 'الكل'>('الكل');
  const [filterDept, setFilterDept] = useState<string>('الكل');
  const [sortField, setSortField] = useState<SortField>('entryTime');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [isPrinting, setIsPrinting] = useState(false);

  const activeVisits = mockVisits.filter(
    (v) => v.status === 'داخل' || v.status === 'منتهية الصلاحية'
  );

  const todayVisits = mockVisits.filter((v) => v.entryTime.startsWith('2026-08-09'));
  const overstayVisits = activeVisits.filter((v) => v.status === 'منتهية الصلاحية');
  const todayExited = todayVisits.filter((v) => v.status === 'خرج');

  const visitorTypes: (VisitorType | 'الكل')[] = ['الكل', 'زائر', 'مورد', 'مقاول', 'عميل', 'سائق شاحنة', 'عامل خارجي'];
  const gates: (GateNumber | 'الكل')[] = ['الكل', 'البوابة الرئيسية', 'بوابة الشاحنات', 'البوابة الخلفية'];
  const departments = ['الكل', ...Array.from(new Set(activeVisits.map((v) => v.hostDepartment)))];

  const filtered = activeVisits
    .filter((v) => {
      const q = searchQuery.toLowerCase();
      const matchSearch =
        !q ||
        v.visitorName.includes(searchQuery) ||
        v.company.includes(searchQuery) ||
        v.nationalId.includes(q) ||
        v.hostDepartment.includes(searchQuery);
      const matchType = filterType === 'الكل' || v.type === filterType;
      const matchGate = filterGate === 'الكل' || v.gate === filterGate;
      const matchDept = filterDept === 'الكل' || v.hostDepartment === filterDept;
      return matchSearch && matchType && matchGate && matchDept;
    })
    .sort((a, b) => {
      let cmp = 0;
      if (sortField === 'entryTime') {
        cmp = new Date(a.entryTime).getTime() - new Date(b.entryTime).getTime();
      } else if (sortField === 'visitorName') {
        cmp = a.visitorName.localeCompare(b.visitorName, 'ar');
      } else if (sortField === 'hostDepartment') {
        cmp = a.hostDepartment.localeCompare(b.hostDepartment, 'ar');
      } else if (sortField === 'type') {
        cmp = a.type.localeCompare(b.type, 'ar');
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const handlePrintEvacuation = () => {
    setIsPrinting(true);
    // TODO: Backend — GET /api/visits/evacuation-report?status=inside generates printable PDF
    setTimeout(() => {
      setIsPrinting(false);
      window.print();
      toast.success(`تم تحضير تقرير الإخلاء — ${activeVisits.length} شخص داخل المصنع`);
    }, 800);
  };

  const SortButton = ({ field, label }: { field: SortField; label: string }) => (
    <button
      onClick={() => handleSort(field)}
      className={`flex items-center gap-1 text-xs font-semibold uppercase tracking-wide transition-colors ${
        sortField === field ? 'text-accent' : 'text-muted-foreground hover:text-foreground'
      }`}
    >
      {label}
      <ArrowUpDown size={10} className={sortField === field ? 'text-accent' : 'text-muted-foreground'} />
    </button>
  );

  return (
    <div className="min-h-screen bg-background p-4 xl:p-6" dir="rtl">
      <Toaster position="bottom-right" richColors />

      {/* Print area */}
      <div className="print-list">
        <div style={{ fontFamily: 'Cairo, sans-serif', direction: 'rtl' }}>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>تقرير الإخلاء الطارئ</h1>
          <p style={{ color: '#666', marginBottom: '4px' }}>تاريخ الطباعة: 09/08/2026 — 11:12</p>
          <p style={{ color: '#666', marginBottom: '16px' }}>إجمالي الموجودين: {activeVisits.length} شخص</p>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f0f0f0' }}>
                {['الاسم', 'الجهة', 'النوع', 'القسم المضيف', 'البوابة', 'وقت الدخول', 'المدة', 'الكارت'].map((h) => (
                  <th key={`ph-${h}`} style={{ padding: '8px', border: '1px solid #ccc', textAlign: 'right', fontSize: '12px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {activeVisits.map((v, i) => (
                <tr key={`pr-${v.id}`} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                  <td style={{ padding: '8px', border: '1px solid #ccc', fontSize: '12px' }}>{v.visitorName}</td>
                  <td style={{ padding: '8px', border: '1px solid #ccc', fontSize: '12px' }}>{v.company}</td>
                  <td style={{ padding: '8px', border: '1px solid #ccc', fontSize: '12px' }}>{v.type}</td>
                  <td style={{ padding: '8px', border: '1px solid #ccc', fontSize: '12px' }}>{v.hostDepartment}</td>
                  <td style={{ padding: '8px', border: '1px solid #ccc', fontSize: '12px' }}>{v.gate}</td>
                  <td style={{ padding: '8px', border: '1px solid #ccc', fontSize: '12px' }}>{formatTime(v.entryTime)}</td>
                  <td style={{ padding: '8px', border: '1px solid #ccc', fontSize: '12px' }}>{computeDuration(v.entryTime)}</td>
                  <td style={{ padding: '8px', border: '1px solid #ccc', fontSize: '12px' }}>{v.badgeNumber}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Page header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-success/15 border border-success/30 flex items-center justify-center">
            <Users size={20} className="text-success" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">الموجودون داخل المصنع الآن</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
              <p className="text-sm text-muted-foreground">آخر تحديث: 11:12:07 — يتجدد تلقائياً</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => toast.info('جاري تحديث البيانات...')}
            className="flex items-center gap-2 px-4 py-2.5 bg-card border border-border rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:border-accent/40 transition-all duration-150 active:scale-95"
          >
            <RefreshCw size={14} />
            تحديث
          </button>
          <button
            onClick={handlePrintEvacuation}
            disabled={isPrinting}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm transition-all duration-150 active:scale-95 ${
              isPrinting
                ? 'bg-danger/50 text-danger-foreground cursor-wait'
                : 'bg-danger text-danger-foreground hover:opacity-90'
            }`}
          >
            {isPrinting ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Printer size={14} />
            )}
            {isPrinting ? 'جاري التحضير...' : 'طباعة تقرير الإخلاء'}
          </button>
        </div>
      </div>

      {/* KPI Bento Grid — 4 cards, grid-cols-4 single row */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        {/* Hero: Currently inside */}
        <div className="col-span-2 xl:col-span-1 bg-card border border-success/30 rounded-2xl p-5 relative overflow-hidden">
          <div className="blob-accent absolute inset-0 pointer-events-none" />
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2">الموجودون الآن</p>
          <p className="text-hero text-success tabular-nums">{activeVisits.length}</p>
          <p className="text-sm text-muted-foreground mt-1">شخص داخل المصنع</p>
          <div className="flex items-center gap-1.5 mt-3">
            <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
            <span className="text-xs text-success">مباشر</span>
          </div>
        </div>

        {/* Today entries */}
        <div className="bg-card border border-border rounded-2xl p-5">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2">دخلوا اليوم</p>
          <p className="text-4xl font-bold text-foreground tabular-nums">{todayVisits.length}</p>
          <p className="text-sm text-muted-foreground mt-1">إجمالي الزيارات</p>
          <div className="flex items-center gap-1.5 mt-3">
            <CheckCircle size={12} className="text-success" />
            <span className="text-xs text-muted-foreground">{todayExited.length} خرجوا</span>
          </div>
        </div>

        {/* Overstay */}
        <div className={`bg-card rounded-2xl p-5 ${overstayVisits.length > 0 ? 'border-2 border-warning/50' : 'border border-border'}`}>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2">منتهية الصلاحية</p>
          <p className={`text-4xl font-bold tabular-nums ${overstayVisits.length > 0 ? 'text-warning' : 'text-muted-foreground'}`}>
            {overstayVisits.length}
          </p>
          <p className="text-sm text-muted-foreground mt-1">تجاوزوا وقت الزيارة</p>
          {overstayVisits.length > 0 && (
            <div className="flex items-center gap-1.5 mt-3">
              <AlertTriangle size={12} className="text-warning" />
              <span className="text-xs text-warning">تحتاج متابعة فورية</span>
            </div>
          )}
        </div>

        {/* By gate */}
        <div className="bg-card border border-border rounded-2xl p-5">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2">توزيع البوابات</p>
          <div className="space-y-1.5 mt-1">
            {[
              { label: 'الرئيسية', gate: 'البوابة الرئيسية' },
              { label: 'الشاحنات', gate: 'بوابة الشاحنات' },
              { label: 'الخلفية', gate: 'البوابة الخلفية' },
            ].map(({ label, gate }) => {
              const count = activeVisits.filter((v) => v.gate === gate).length;
              const pct = activeVisits.length > 0 ? (count / activeVisits.length) * 100 : 0;
              return (
                <div key={`gate-stat-${gate}`}>
                  <div className="flex justify-between items-center mb-0.5">
                    <span className="text-xs text-muted-foreground">{label}</span>
                    <span className="text-xs font-bold text-foreground tabular-nums">{count}</span>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-accent rounded-full transition-all duration-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Chart + Type breakdown */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
        <div className="xl:col-span-2 bg-card border border-border rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-foreground">حجم الزيارات بالساعة</h3>
              <p className="text-xs text-muted-foreground mt-0.5">اليوم — 09 أغسطس 2026</p>
            </div>
          </div>
          <HourlyVisitsChart />
        </div>

        <div className="bg-card border border-border rounded-2xl p-5">
          <h3 className="font-bold text-foreground mb-4">توزيع أنواع الزوار</h3>
          <div className="space-y-3">
            {(['مقاول', 'مورد', 'زائر', 'سائق شاحنة', 'عامل خارجي', 'عميل'] as const).map((type) => {
              const count = activeVisits.filter((v) => v.type === type).length;
              const total = activeVisits.length || 1;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={`type-dist-${type}`}>
                  <div className="flex items-center justify-between mb-1">
                    <TypeBadge type={type} size="sm" />
                    <span className="text-xs font-bold text-foreground tabular-nums">{count}</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-accent/70 rounded-full transition-all duration-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-2xl p-4 mb-4">
        <div className="flex flex-col lg:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="بحث بالاسم أو الجهة أو الرقم القومي..."
              className="w-full bg-input border border-border rounded-xl pr-9 pl-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X size={14} />
              </button>
            )}
          </div>

          {/* Type filter */}
          <div className="relative">
            <Filter size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as VisitorType | 'الكل')}
              className="bg-input border border-border rounded-xl pr-9 pl-8 py-2.5 text-sm text-foreground appearance-none focus:outline-none focus:ring-2 focus:ring-ring min-w-[140px]"
            >
              {visitorTypes.map((t) => (
                <option key={`filter-type-${t}`} value={t}>{t === 'الكل' ? 'كل الأنواع' : t}</option>
              ))}
            </select>
            <ChevronDown size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>

          {/* Gate filter */}
          <div className="relative">
            <DoorOpen size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <select
              value={filterGate}
              onChange={(e) => setFilterGate(e.target.value as GateNumber | 'الكل')}
              className="bg-input border border-border rounded-xl pr-9 pl-8 py-2.5 text-sm text-foreground appearance-none focus:outline-none focus:ring-2 focus:ring-ring min-w-[160px]"
            >
              {gates.map((g) => (
                <option key={`filter-gate-${g}`} value={g}>{g === 'الكل' ? 'كل البوابات' : g}</option>
              ))}
            </select>
            <ChevronDown size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>

          {/* Dept filter */}
          <div className="relative">
            <Building2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <select
              value={filterDept}
              onChange={(e) => setFilterDept(e.target.value)}
              className="bg-input border border-border rounded-xl pr-9 pl-8 py-2.5 text-sm text-foreground appearance-none focus:outline-none focus:ring-2 focus:ring-ring min-w-[160px]"
            >
              {departments.map((d) => (
                <option key={`filter-dept-${d}`} value={d}>{d === 'الكل' ? 'كل الأقسام' : d}</option>
              ))}
            </select>
            <ChevronDown size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>
        </div>

        {/* Active filter chips */}
        {(filterType !== 'الكل' || filterGate !== 'الكل' || filterDept !== 'الكل' || searchQuery) && (
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <span className="text-xs text-muted-foreground">فلاتر نشطة:</span>
            {searchQuery && (
              <span className="flex items-center gap-1 bg-accent/15 text-accent text-xs px-2 py-1 rounded-full">
                "{searchQuery}"
                <button onClick={() => setSearchQuery('')}><X size={10} /></button>
              </span>
            )}
            {filterType !== 'الكل' && (
              <span className="flex items-center gap-1 bg-accent/15 text-accent text-xs px-2 py-1 rounded-full">
                {filterType}
                <button onClick={() => setFilterType('الكل')}><X size={10} /></button>
              </span>
            )}
            {filterGate !== 'الكل' && (
              <span className="flex items-center gap-1 bg-accent/15 text-accent text-xs px-2 py-1 rounded-full">
                {filterGate}
                <button onClick={() => setFilterGate('الكل')}><X size={10} /></button>
              </span>
            )}
            {filterDept !== 'الكل' && (
              <span className="flex items-center gap-1 bg-accent/15 text-accent text-xs px-2 py-1 rounded-full">
                {filterDept}
                <button onClick={() => setFilterDept('الكل')}><X size={10} /></button>
              </span>
            )}
            <button
              onClick={() => { setSearchQuery(''); setFilterType('الكل'); setFilterGate('الكل'); setFilterDept('الكل'); }}
              className="text-xs text-muted-foreground hover:text-danger transition-colors"
            >
              مسح الكل
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="font-bold text-foreground flex items-center gap-2">
            <Users size={16} className="text-accent" />
            قائمة الموجودين
            <span className="bg-accent/15 text-accent text-xs font-bold px-2 py-0.5 rounded-full tabular-nums">{filtered.length}</span>
          </h3>
          {overstayVisits.length > 0 && (
            <div className="flex items-center gap-2 bg-warning/10 border border-warning/30 rounded-xl px-3 py-1.5">
              <AlertTriangle size={14} className="text-warning" />
              <span className="text-xs font-semibold text-warning">{overstayVisits.length} زيارة منتهية الصلاحية تحتاج متابعة</span>
            </div>
          )}
        </div>

        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="bg-muted/40 border-b border-border">
                <th className="text-right px-4 py-3 w-8">
                  <span className="text-xs text-muted-foreground">#</span>
                </th>
                <th className="text-right px-4 py-3">
                  <SortButton field="visitorName" label="الاسم" />
                </th>
                <th className="text-right px-4 py-3">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">الجهة</span>
                </th>
                <th className="text-right px-4 py-3">
                  <SortButton field="type" label="النوع" />
                </th>
                <th className="text-right px-4 py-3">
                  <SortButton field="hostDepartment" label="القسم المضيف" />
                </th>
                <th className="text-right px-4 py-3">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">البوابة</span>
                </th>
                <th className="text-right px-4 py-3">
                  <SortButton field="entryTime" label="وقت الدخول" />
                </th>
                <th className="text-right px-4 py-3">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">المدة</span>
                </th>
                <th className="text-right px-4 py-3">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">الكارت</span>
                </th>
                <th className="text-right px-4 py-3">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">الحالة</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-16">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-12 h-12 rounded-2xl bg-muted flex items-center justify-center">
                        <Users size={24} className="text-muted-foreground" />
                      </div>
                      <p className="text-foreground font-semibold">لا يوجد موجودون مطابقون للفلاتر</p>
                      <p className="text-sm text-muted-foreground">جرّب تغيير معايير البحث أو الفلترة</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((visit, index) => (
                  <tr
                    key={`inside-row-${visit.id}`}
                    className={`border-b border-border last:border-0 transition-colors duration-100 hover:bg-muted/30 ${
                      isOverstay(visit) ? 'bg-warning/5' : index % 2 === 0 ? '' : 'bg-muted/10'
                    }`}
                  >
                    <td className="px-4 py-3 text-xs text-muted-foreground tabular-nums">{index + 1}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                          <Shield size={12} className="text-accent" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-foreground">{visit.visitorName}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Phone size={9} />
                            {visit.mobile}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-foreground">{visit.company}</p>
                    </td>
                    <td className="px-4 py-3">
                      <TypeBadge type={visit.type} size="sm" />
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-foreground">{visit.hostDepartment}</p>
                      <p className="text-xs text-muted-foreground">{visit.hostEmployee}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <DoorOpen size={11} />
                        {visit.gate.replace('البوابة ', '').replace('بوابة ', '')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground tabular-nums">
                        <Clock size={12} className="text-accent" />
                        {formatTime(visit.entryTime)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm font-semibold tabular-nums ${isOverstay(visit) ? 'text-warning' : 'text-foreground'}`}>
                        {computeDuration(visit.entryTime)}
                      </span>
                      {isOverstay(visit) && (
                        <p className="text-xs text-warning flex items-center gap-1 mt-0.5">
                          <AlertTriangle size={9} />
                          تجاوز الوقت
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-1 text-xs font-mono text-accent">
                        <CreditCard size={10} />
                        {visit.badgeNumber}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={visit.status} size="sm" />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {filtered.length > 0 && (
          <div className="px-5 py-3 border-t border-border flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              عرض <span className="text-foreground font-semibold tabular-nums">{filtered.length}</span> من أصل{' '}
              <span className="text-foreground font-semibold tabular-nums">{activeVisits.length}</span> موجود
            </p>
            {visit => (
              <div className="flex items-center gap-2">
                <Car size={12} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground">
                  <span className="text-foreground font-semibold tabular-nums">
                    {activeVisits.filter((v) => v.vehiclePlate).length}
                  </span>{' '}
                  مركبة مسجلة
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}